import _ from "lodash";
import ko from "knockout";
import include from "bindingHandlers/include";

/**
 * (Public) Gets the component context from the context parents
 *
 * @param   bindingContext (object) The binding context of the handler making
 *                                  the call
 *
 * @return                 (string) The component name or undefined if it is
 *                                  not in the context of a component
 **/
const getContext = function (bindingContext) {
    let context;
    if (bindingContext) {
        _.each(bindingContext.$parents, function (parent) {
            if (parent) {
                const templateContext = parent[include.TEMPLATE_CONTEXT_KEY];
                if (templateContext) {
                    context = templateContext;
                }
            }
        });
    }
    return context;
};

/**
 * (Public) Determines if the template should be loaded in the context of a
 * component, and if so, appends the template context key to the viewModel.
 *
 * @param   viewModel      (object) The viewModel that will be passed into the
 *                                  include binding
 * @param   bindingContext (object) The binding context of the handler making
 *                                  the call
 *
 * @return                 (object) viewModel, either original or with a template
 *                                  context key appended
 **/
const decorateWithTemplateContext = function (viewModel, bindingContext) {
    const context = getContext(bindingContext);

    if (context) {
        viewModel[include.TEMPLATE_CONTEXT_KEY] = context;
    }
    return viewModel;
};

/**
 * (Public) Checks the component context and adjusts the template name so it
 * can be loaded properly
 *
 * @param   templateName   (string) The template string
 *
 * @param   bindingContext (object) The binding context of the handler making
 *                                  the call
 *
 * @return                 (object) The template name in the format needed
 *                                  in order for it to be properly loaded.
 **/
const adjustTemplateNameForContext = function (templateName, bindingContext) {
    const context = getContext(bindingContext);

    if (templateName && context) {
        const componentRegex = new RegExp("^" + context + "/");
        templateName = templateName.replace(
            componentRegex,
            "components/" + context + "/templates/"
        );
        templateName = templateName.replace(/^core\//, "templates/");
        if (templateName == "main") {
            templateName = "components/" + context + "/templates/main";
        }
    }
    return templateName;
};

const ensureObservable = (obs, defaultValue) => {
    if (ko.isObservable(obs)) {
        return obs;
    }

    if (obs === undefined) {
        obs = ko.unwrap(defaultValue);
    }

    return ko.observable(obs);
};

const requireObservable = (obs) => {
    if (ko.isObservable(obs)) {
        return obs;
    }

    throw new Error("must be an observable");
};

export default {
    ensureObservable: ensureObservable,
    requireObservable: requireObservable,
    getContext: getContext,
    decorateWithTemplateContext: decorateWithTemplateContext,
    adjustTemplateNameForContext: adjustTemplateNameForContext,
};
